<template>
  <div class="edit-profile-page">
    <!-- Custom Header -->
    <header class="ep-header">
      <button class="ep-header__back" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </button>
      <h1 class="ep-header__title">{{ $lang.editProfile_title || 'EDIT PROFILE' }}</h1>
      <button class="ep-header__save" @click="handleSave">
        {{ $lang.editProfile_save || 'SAVE' }}
      </button>
    </header>
    <div class="ep-header__spacer"></div>

    <div class="ep-content">
      <!-- Avatar Section -->
      <div class="ep-avatar" @click="$jumpTo('/avatar')">
        <div class="ep-avatar__wrapper">
          <img
            v-if="headUrl != null"
            :src="require(`@/assets/img/avatar/avatar_${headUrl}.jpg`)"
            class="ep-avatar__img"
          />
          <div v-else class="ep-avatar__placeholder"></div>
          <div class="ep-avatar__camera">
            <van-icon name="photograph" size="16" color="#1a1d29" />
          </div>
        </div>
      </div>

      <!-- User ID (readonly + copy) -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_user_id || 'User ID' }}</label>
        <div class="ep-field__input ep-field__input--readonly">
          <span>ID: {{ inviteCode }}</span>
          <button class="ep-field__copy" @click="copyId">
            <van-icon name="records" size="16" color="#94a3b8" />
          </button>
        </div>
      </div>

      <!-- Phone -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_phone || 'Phone' }}</label>
        <div class="ep-field__input ep-field__input--readonly">
          <span>{{ phone || '--' }}</span>
        </div>
      </div>

      <!-- Birthday -->
      <div class="ep-field">
        <label class="ep-field__label">{{ $lang.editProfile_birthday || 'Birthday' }}</label>
        <div
          class="ep-field__input"
          style="cursor: pointer"
          @click="showBirthdayPicker = true"
        >
          <span :style="{ color: birthday ? '#ffffff' : '#4b5563' }">
            {{ birthday || ($lang.editProfile_birthday_ph || 'Select birthday') }}
          </span>
          <van-icon name="calendar-o" size="16" color="#94a3b8" />
        </div>
      </div>
    </div>

    <DatePickerPopup
      v-model="showBirthdayPicker"
      :min-date="minDate"
      :max-date="maxDate"
      :default-date="pickerDate"
      :confirm-text="$lang.editProfile_save || 'Confirm'"
      :cancel-text="$lang.common_cancel || 'Cancel'"
      @confirm="onBirthdayConfirm"
    />
  </div>
</template>

<script>
import { Init, ChangeExtend } from '@/api/common'
import DatePickerPopup from '@/components/DatePickerPopup'

export default {
  name: 'EditProfile',
  components: { DatePickerPopup },
  data() {
    return {
      headUrl: null,
      inviteCode: '',
      phone: '',
      birthday: '',
      showBirthdayPicker: false,
      pickerDate: new Date(),
      minDate: new Date(1950, 0, 1),
      maxDate: new Date(),
      saving: false
    }
  },
  mounted() {
    this.loadProfile()
  },
  methods: {
    async loadProfile() {
      try {
        const data = await Init()
        if (data.status === 'ok') {
          this.headUrl = data.content.headUrl
          this.inviteCode = data.content.inviteCode || ''
          this.phone = data.content.phone || data.content.account || ''
          this.birthday = data.content.birthday || ''
          if (this.birthday) {
            const d = new Date(this.birthday)
            if (!isNaN(d.getTime())) this.pickerDate = d
          }
        }
      } catch (e) {
        console.error('Init error', e)
      }
    },
    onBirthdayConfirm(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      this.birthday = `${y}-${m}-${d}`
      this.showBirthdayPicker = false
    },
    copyId() {
      const text = this.inviteCode
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.$toast({ message: this.$lang.Sucesso, icon: 'success' })
    },
    async handleSave() {
      if (this.saving) return
      this.saving = true
      try {
        const res = await ChangeExtend({ birthday: this.birthday || '' })
        if (res.status === 'ok') {
          this.$toast({ message: this.$lang.Sucesso || 'Saved', icon: 'success' })
          this.$router.go(-1)
        } else {
          this.$toast({ message: res.msg || 'Save failed', icon: 'cross' })
        }
      } catch (e) {
        console.error('ChangeExtend error', e)
        this.$toast({ message: 'Save failed', icon: 'cross' })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.edit-profile-page {
  background-color: #1a1d29;
  min-height: 100vh;
  color: #ffffff;
}

.ep-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: #1a1d29;
  z-index: 999;

  &__back {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
  }

  &__title {
    font-size: 18px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #ffffff;
    margin: 0;
  }

  &__save {
    background: none;
    border: none;
    color: #24ee89;
    font-size: 14px;
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
    padding: 0;
  }

  &__spacer {
    height: 55px;
  }
}

.ep-content {
  padding-bottom: 24px;
}

.ep-avatar {
  display: flex;
  justify-content: center;
  margin: 24px auto;
  cursor: pointer;

  &__wrapper {
    position: relative;
    width: 128px;
    height: 128px;
    border-radius: 50%;
    border: 4px solid #2a2e3d;
    overflow: visible;
  }

  &__img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: #2a2e3d;
  }

  &__camera {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #24ee89;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #1a1d29;
  }
}

.ep-field {
  margin-bottom: 20px;
  padding: 0 16px;

  &__label {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #94a3b8;
    margin-bottom: 8px;
  }

  &__input {
    background-color: #252936;
    border: 1px solid #3f4451;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #ffffff;
    font-size: 14px;
    transition: border-color 0.2s ease;

    &:focus-within {
      border-color: #24ee89;
      box-shadow: 0 0 0 2px rgba(36, 238, 137, 0.15);
    }

    &--readonly {
      opacity: 0.7;

      &:focus-within {
        border-color: #3f4451;
        box-shadow: none;
      }
    }
  }

  &__text {
    background: transparent;
    border: none;
    color: #ffffff;
    width: 100%;
    outline: none;
    font-size: 14px;
    padding: 0;
    margin: 0;

    &::placeholder {
      color: #4b5563;
    }
  }

  &__copy {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: 8px;
  }
}
</style>
